import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { getFabricDesc, getRailDiameter, calculateChainLink } from '../utils/chainLinkCalc';
import { POST_OPTIONS, BARB_WIRE_OPTIONS, getGatePostOptions, getCantileverGateSKU, getSwingGateSize } from '../data/estimatorSKUs';

const TIE_ON_DIAMETERS = ['1-5/8"', '2"', '2-1/2"', '3"', '4"'];
const HEIGHTS_LIGHT = ['3', '3.5', '4', '5', '6'];
const HEIGHTS_HEAVY = ['3', '3.5', '4', '5', '6', '7', '8', '10', '12'];

let runIdCounter = 1;
const newRun = () => ({ id: runIdCounter++, length: '' });

function ProgressBar({ step, gateModalOpen }) {
  const steps = [
    { n: 1, label: 'Basic Specs' },
    { n: 2, label: 'Configuration' },
    { n: 3, label: 'Dimensions' },
    { n: 4, label: 'Gate Details' },
  ];
  const active = gateModalOpen ? 4 : step;
  return (
    <div className="flex items-center justify-between mb-8">
      {steps.map((s, i) => {
        const isActive = active === s.n;
        const isDone = active > s.n;
        return (
        <React.Fragment key={s.n}>
          <div className="flex flex-col items-center">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-colors"
              style={{
                background: isActive ? '#0077B6' : isDone ? '#0F6E56' : '#E3ECF5',
                color: isActive || isDone ? '#FFFFFF' : '#6B7C93',
                boxShadow: isActive ? '0 0 0 4px rgba(0,119,182,0.2)' : 'none',
              }}
            >
              {isDone ? '✓' : s.n}
            </div>
            <p className="text-xs mt-1 font-semibold"
              style={{ color: isActive ? '#03045E' : isDone ? '#0F6E56' : '#6B7C93' }}>
              {s.label}
            </p>
          </div>
          {i < steps.length - 1 && (
            <div className="flex-1 h-1 mx-2 rounded"
              style={{ background: isDone ? '#0077B6' : '#E3ECF5' }} />
          )}
        </React.Fragment>
        );
      })}
    </div>
  );
}

function Estimator({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useData();
  const returnTo = location?.state?.returnTo || null;
  const previousSpecs = location?.state?.previousSpecs || null;
  const docPriceLevel = location?.state?.priceLevel || 'A';
  const userName = user?.email?.split('@')[0]?.toUpperCase();

  const [step, setStep] = useState(1);

  // Screen 1
  const [finish, setFinish] = useState(() => previousSpecs?.finish || 'Galvanized');
  const [fenceClass, setFenceClass] = useState(() => previousSpecs?.fenceClass || 'Residential');
  const [height, setHeight] = useState(() => previousSpecs?.height || '4');

  // Screen 2
  const [topRail, setTopRail] = useState(() => previousSpecs?.topRail ?? true);
  const [bottomRail, setBottomRail] = useState(() => previousSpecs?.bottomRail ?? false);
  const [midRail, setMidRail] = useState(() => previousSpecs?.midRail ?? false);
  const [braceRail, setBraceRail] = useState(() => previousSpecs?.braceRail ?? false);
  const [barbWire, setBarbWire] = useState(() => previousSpecs?.barbWire ?? false);
  const [coilTop, setCoilTop] = useState(() => previousSpecs?.coilTop ?? false);
  const [coilBottom, setCoilBottom] = useState(() => previousSpecs?.coilBottom ?? false);
  const [postSpacing, setPostSpacing] = useState(() => previousSpecs?.postSpacing ?? 10);
  const [endPostSize, setEndPostSize] = useState(() => previousSpecs?.endPostSize || '');
  const [cornerPostSize, setCornerPostSize] = useState(() => previousSpecs?.cornerPostSize || '');
  const [linePostSize, setLinePostSize] = useState(() => previousSpecs?.linePostSize || '');

  // Screen 3
  const [runs, setRuns] = useState(() => {
    if (previousSpecs?.runs?.length > 0) {
      // Reassign IDs to avoid collisions with the module-level counter
      return previousSpecs.runs.map(r => ({ id: runIdCounter++, length: r.length ?? '' }));
    }
    return [newRun()];
  });
  const [totalEnds, setTotalEnds] = useState(() => previousSpecs?.totalEnds ?? 0);
  const [totalCorners, setTotalCorners] = useState(() => previousSpecs?.totalCorners ?? 0);
  const [tieOnsEnabled, setTieOnsEnabled] = useState(() => previousSpecs?.tieOnsEnabled ?? false);
  const [tieOns, setTieOns] = useState(() => previousSpecs?.tieOns || []);
  const [gates, setGates] = useState(() => previousSpecs?.gates || []);

  // Screen 4 modal
  const [gateModalOpen, setGateModalOpen] = useState(false);
  const [editingGateIdx, setEditingGateIdx] = useState(null);
  const [gateForm, setGateForm] = useState({
    type: 'Single Swing', opening: '', postSize: '', latchPostSize: '',
  });

  const runInputRefs = useRef({});

  // ── Derived booleans ──
  const barbWireAvail = fenceClass === 'Commercial' && parseFloat(height) >= 5;
  const braceRailAvail = fenceClass === 'Commercial' || parseFloat(height) >= 4;
  const effectiveBarbWire = barbWire && barbWireAvail;

  const getEndCornerOpts = (cls, h, barb) => {
    if (barb && BARB_WIRE_OPTIONS[h]) return BARB_WIRE_OPTIONS[h].endCorner.options;
    return POST_OPTIONS[cls]?.[h]?.endCorner?.options || [];
  };

  // ── Auto-reset post options when Screen 1 or barbWire changes ──
  // On a real change to grade/height/barb-wire, the post sizes reset to the
  // default (options[0]) so the post LENGTH tracks the new height. (Preserving
  // the prior pick caused the 6'→still-6' bug: a 6' post seeded at the initial
  // height='4' stayed selected at 6' because it's also a valid 6'-height option.)
  //
  // Two exceptions, both handled via a deps-key guard rather than a first-run
  // boolean (which StrictMode's dev double-invoke would consume, clobbering a
  // restored estimate on the second invocation):
  //   1. First run — keep restored previousSpecs selections when still valid.
  //   2. Re-run with an unchanged key (StrictMode re-invoke) — do nothing.
  const lastPostResetKey = useRef(null);
  useEffect(() => {
    const endCornerOpts = getEndCornerOpts(fenceClass, height, effectiveBarbWire);
    const lineOpts = POST_OPTIONS[fenceClass]?.[height]?.line?.options || [];
    const key = `${fenceClass}|${height}|${effectiveBarbWire}`;

    if (lastPostResetKey.current === null) {
      // First run: preserve a restored saved-estimate selection if it's still a
      // valid option, otherwise fall back to the default.
      lastPostResetKey.current = key;
      setEndPostSize(prev => endCornerOpts.includes(prev) ? prev : (endCornerOpts[0] || ''));
      setCornerPostSize(prev => endCornerOpts.includes(prev) ? prev : (endCornerOpts[0] || ''));
      setLinePostSize(prev => lineOpts.includes(prev) ? prev : (lineOpts[0] || ''));
      return;
    }

    // Same key (e.g. StrictMode double-invoke) — nothing actually changed.
    if (lastPostResetKey.current === key) return;

    // Real user change to grade/height/barb-wire — reset to the default post.
    lastPostResetKey.current = key;
    setEndPostSize(endCornerOpts[0] || '');
    setCornerPostSize(endCornerOpts[0] || '');
    setLinePostSize(lineOpts[0] || '');
  }, [fenceClass, height, effectiveBarbWire]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset unavailable options ──
  useEffect(() => { if (!barbWireAvail) setBarbWire(false); }, [barbWireAvail]);
  useEffect(() => { if (!braceRailAvail) setBraceRail(false); }, [braceRailAvail]);

  const handleClassChange = (cls) => {
    setFenceClass(cls);
    if (cls === 'Residential' && !HEIGHTS_LIGHT.includes(height)) setHeight('4');
  };

  // ── Gate modal helpers ──
  const openAddGate = () => {
    const opts = getGatePostOptions(fenceClass, height, 'Single Swing', '', finish);
    setGateForm({ type: 'Single Swing', opening: '', postSize: opts[0] || '', latchPostSize: opts[0] || '' });
    setEditingGateIdx(null);
    setGateModalOpen(true);
  };

  const openEditGate = (idx) => {
    setGateForm({ ...gates[idx] });
    setEditingGateIdx(idx);
    setGateModalOpen(true);
  };

  const handleGateTypeChange = (type) => {
    const opts = getGatePostOptions(fenceClass, height, type, gateForm.opening, finish);
    setGateForm(f => ({ ...f, type, postSize: opts[0] || '', latchPostSize: opts[0] || '' }));
  };

  const handleGateOpeningChange = (opening) => {
    const opts = getGatePostOptions(fenceClass, height, gateForm.type, opening, finish);
    setGateForm(f => ({
      ...f,
      opening,
      postSize: opts.includes(f.postSize) ? f.postSize : (opts[0] || ''),
      latchPostSize: opts.includes(f.latchPostSize) ? f.latchPostSize : (opts[0] || ''),
    }));
  };

  const handleSaveGate = () => {
    if (!gateForm.opening || !gateForm.postSize) return;
    if (editingGateIdx !== null) {
      setGates(prev => prev.map((g, i) => i === editingGateIdx ? { ...gateForm } : g));
    } else {
      setGates(prev => [...prev, { ...gateForm }]);
    }
    setGateModalOpen(false);
  };

  // ── Run helpers ──
  const updateRun = (id, length) => setRuns(prev => prev.map(r => r.id === id ? { ...r, length } : r));
  const removeRun = (id) => { if (runs.length > 1) setRuns(prev => prev.filter(r => r.id !== id)); };
  const addRun = () => {
    const nr = newRun();
    setRuns(prev => [...prev, nr]);
    setTimeout(() => runInputRefs.current[nr.id]?.focus(), 50);
  };
  const handleRunKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRun(); } };

  // ── Tie-on helpers ──
  const addTieOn = () => setTieOns(prev => [...prev, { diameter: '2"', qty: 1 }]);
  const updateTieOn = (i, field, val) => setTieOns(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  const removeTieOn = (i) => setTieOns(prev => prev.filter((_, idx) => idx !== i));

  // ── Calculate & Navigate ──
  const buildSpecs = () => ({
    // Discriminator, as the other three estimators have always written. Chain
    // link's settings used to carry no kind and be recognised by its absence;
    // that fallback stays for old documents only (see utils/estimates.js).
    kind: 'chainlink',
    finish, fenceClass, height,
    topRail, bottomRail, midRail, braceRail,
    barbWire: effectiveBarbWire, coilTop, coilBottom,
    postSpacing,
    endPostSize, cornerPostSize, linePostSize,
    runs, totalEnds, totalCorners,
    tieOnsEnabled, tieOns,
    gates,
  });

  const handleCalculate = () => {
    if (returnTo && (!products || products.length === 0)) {
      alert('Products are still loading. Please wait a moment and try again.');
      return;
    }
    const specs = buildSpecs();
    if (returnTo) {
      // Calculate at the document's price level. The receiving page re-prices
      // against its products list as well, but computing the right level here
      // keeps the data consistent and avoids a wrong-price flash.
      const { items } = calculateChainLink(specs, products, docPriceLevel);
      navigate(returnTo, {
        state: {
          lineItems: items,
          estimatorSpecs: specs,
          priceLevel: docPriceLevel,
        },
      });
    } else {
      navigate('/estimator-results', { state: specs });
    }
  };

  // ── Derived display ──
  const totalFootage = runs.reduce((s, r) => s + (parseFloat(r.length) || 0), 0);
  const fabricDesc = getFabricDesc(finish, fenceClass, height);
  const railDiam = getRailDiameter(fenceClass, height);
  const endCornerOpts = getEndCornerOpts(fenceClass, height, effectiveBarbWire);
  const lineOpts = POST_OPTIONS[fenceClass]?.[height]?.line?.options || [];
  const gatePostOpts = getGatePostOptions(fenceClass, height, gateForm.type, gateForm.opening, finish);

  const canCalc = runs.some(r => parseFloat(r.length) > 0) && endPostSize && linePostSize;

  const screen2Summary = [
    topRail && `Top Rail (${railDiam})`,
    bottomRail && `Bottom Rail (${railDiam})`,
    midRail && `Mid Rail (${railDiam})`,
    braceRail && `Brace Rail (${railDiam})`,
    effectiveBarbWire && 'Barb Wire',
    coilTop && 'Coil Wire Top',
    coilBottom && 'Coil Wire Bottom',
  ].filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header card — flat NewDocument-style (replaces the old blue top bar) */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>Chain Link Estimator</h1>
              {userName && (
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                  Salesperson: <span style={{ color: '#0077B6' }}>{userName}</span>
                </p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {returnTo && (
              <button
                onClick={() => navigate(returnTo, { state: { keepCurrentItems: true } })}
                style={{ background: '#FDF1DD', border: '1px solid #E6C998', color: '#854F0B', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
              >
                ← Back to {returnTo.includes('invoice') ? 'Invoice' : 'Quote'} (no changes)
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
            >
              ← Dashboard
            </button>
          </div>
        </div>

        {previousSpecs && (
          <div style={{ background: '#FAEEDA', border: '1px solid #EBD9B4', borderRadius: 16, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>🔧</span>
            <div>
              <p style={{ color: '#854F0B', fontWeight: 700, margin: 0, fontSize: 14 }}>Modifying existing estimate</p>
              <p style={{ color: '#9A6B2E', fontSize: 13, margin: '2px 0 0 0' }}>Make your changes and click Calculate to update the document. Non-estimator items will be preserved.</p>
            </div>
          </div>
        )}
        <ProgressBar step={step} gateModalOpen={gateModalOpen} />

        {/* ══════════ SCREEN 1 ══════════ */}
        {step === 1 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 24 }}>
            <h2 style={{ color: '#03045E', fontSize: 20, fontWeight: 700, margin: 0 }}>Step 1 — Basic Specifications</h2>
            <p style={{ color: '#6B7C93', fontSize: 14, margin: '4px 0 24px 0' }}>Choose finish, class, and height to get started.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Finish */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>Finish</label>
                <div className="flex flex-col gap-2">
                  {['Galvanized', 'Black'].map(f => {
                    const on = finish === f;
                    return (
                    <button key={f} onClick={() => setFinish(f)}
                      style={{ textAlign: 'left', padding: '12px 16px', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s',
                        background: on ? '#0077B6' : '#FFFFFF', color: on ? '#FFFFFF' : '#6B7C93', border: `1px solid ${on ? '#0077B6' : '#E3ECF5'}` }}>
                      {f}
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Class */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>Class</label>
                <div className="flex flex-col gap-2">
                  {[
                    { val: 'Residential', label: 'Light (Residential)' },
                    { val: 'Commercial', label: 'Heavy (Commercial)' },
                  ].map(c => {
                    const on = fenceClass === c.val;
                    return (
                    <button key={c.val} onClick={() => handleClassChange(c.val)}
                      style={{ textAlign: 'left', padding: '12px 16px', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s',
                        background: on ? '#0077B6' : '#FFFFFF', color: on ? '#FFFFFF' : '#6B7C93', border: `1px solid ${on ? '#0077B6' : '#E3ECF5'}` }}>
                      {c.label}
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Height */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>Height</label>
                <select value={height} onChange={e => {
                    const h = e.target.value;
                    setHeight(h);
                    // Auto-recommend Brace Rail for fences 6' or taller, but only on actual
                    // user interaction (not during saved-spec restoration) and only if Mid Rail
                    // isn't selected, to respect their mutual exclusion. Never auto-unchecks.
                    if (parseFloat(h) >= 6 && !midRail) setBraceRail(true);
                  }}
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '12px 16px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 12, color: '#03045E', fontSize: 14, fontWeight: 700, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                  {(fenceClass === 'Residential' ? HEIGHTS_LIGHT : HEIGHTS_HEAVY).map(h => (
                    <option key={h} value={h}>{h === '3.5' ? "3'-6\"" : `${h}'`}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 24, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16 }}>
              <p style={{ color: '#03045E', fontWeight: 600, margin: 0 }}>
                Selected: <span style={{ fontWeight: 800, color: '#0077B6' }}>{fenceClass === 'Residential' ? 'Light (Residential)' : 'Heavy (Commercial)'}</span>
                {' — '}<span style={{ fontWeight: 800, color: '#0077B6' }}>{height === '3.5' ? "3'-6\"" : `${height}'`} Height</span>
                {' — '}<span style={{ fontWeight: 800, color: '#0077B6' }}>{finish}</span>
              </p>
            </div>

            <div className="flex justify-end mt-6">
              <button onClick={() => setStep(2)}
                style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 32px', borderRadius: 12, fontSize: 15, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)' }}>
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ══════════ SCREEN 2 ══════════ */}
        {step === 2 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 24 }}>
            <h2 style={{ color: '#03045E', fontSize: 20, fontWeight: 700, margin: 0 }}>Step 2 — Configuration</h2>
            <p style={{ color: '#6B7C93', fontSize: 14, margin: '4px 0 20px 0' }}>Fabric is auto-selected. Choose rails, wire options, and post sizes.</p>

            {/* Fabric Info Box */}
            <div style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <p style={{ color: '#0077B6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 4px 0' }}>Auto-Selected Fabric</p>
              <p style={{ color: '#03045E', fontWeight: 700, fontSize: 16, margin: 0 }}>{fabricDesc}</p>
            </div>

            {/* Rails */}
            <div className="mb-5">
              <p style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>Rails</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  // Top Rail and Coil Wire at Top are mutually exclusive (see Wire section).
                  { label: `Top Rail (${railDiam})`, val: topRail, set: setTopRail, avail: true,
                    conflict: setCoilTop, disabled: coilTop && !topRail,
                    hint: coilTop && !topRail ? 'Coil Wire at Top is selected' : null },
                  // Bottom Rail and Coil Wire at Bottom are mutually exclusive: greyed
                  // out (and click-blocked) while the other is selected; turning it on
                  // auto-clears the other as a backstop.
                  { label: `Bottom Rail (${railDiam})`, val: bottomRail, set: setBottomRail, avail: true,
                    conflict: setCoilBottom, disabled: coilBottom && !bottomRail,
                    hint: coilBottom && !bottomRail ? 'Coil Wire at Bottom is selected' : null },
                  // Mid Rail and Brace Rail are mutually exclusive: greyed out (and
                  // click-blocked) while the other is selected; turning it on
                  // auto-clears the other as a backstop.
                  { label: `Mid Rail (${railDiam})`, val: midRail, set: setMidRail, avail: true,
                    conflict: setBraceRail, disabled: braceRail && !midRail,
                    hint: braceRail && !midRail ? 'Brace Rail is selected' : null },
                  { label: `Brace Rail (${railDiam})`, val: braceRail, set: setBraceRail, avail: braceRailAvail,
                    conflict: setMidRail, disabled: midRail && !braceRail,
                    hint: midRail && !braceRail ? 'Mid Rail is selected' : null,
                    note: parseFloat(height) >= 6 && braceRail ? "(Recommended for fences 6' or taller)" : null },
                ].filter(o => o.avail).map(o => {
                  const toggle = () => {
                    if (o.disabled) return;
                    if (!o.val && o.conflict) o.conflict(false);
                    o.set(!o.val);
                  };
                  return (
                    <label key={o.label}
                      title={o.disabled && o.hint ? `${o.hint} — only one can be selected` : ''}
                      className="flex items-center gap-3 p-3 rounded-xl"
                      style={{
                        border: `1px solid ${o.disabled ? '#E3ECF5' : o.val ? '#0077B6' : '#E3ECF5'}`,
                        background: o.disabled ? '#CDE7F5' : o.val ? '#E6F1FB' : '#FFFFFF',
                        opacity: o.disabled ? 0.5 : 1,
                        cursor: o.disabled ? 'not-allowed' : 'pointer',
                      }}>
                      <input type="checkbox" checked={o.val} onChange={toggle} disabled={o.disabled} style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                      <span style={{ color: '#03045E', fontWeight: 600, fontSize: 14 }}>{o.label}</span>
                      {o.note && <span style={{ color: '#0077B6', fontSize: 12, marginLeft: 8, fontStyle: 'italic' }}>{o.note}</span>}
                      {o.hint && <span style={{ color: '#6B7C93', fontSize: 12, marginLeft: 8, fontStyle: 'italic' }}>{o.hint}</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Wire */}
            <div className="mb-5">
              <p style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>Wire</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  // Mutually exclusive with Top Rail (see Rails section above).
                  { label: 'Coil Wire at Top', val: coilTop, set: setCoilTop, avail: true,
                    conflict: setTopRail, disabled: topRail && !coilTop,
                    hint: topRail && !coilTop ? 'Top Rail is selected' : null },
                  // Mutually exclusive with Bottom Rail (see Rails section above).
                  { label: 'Coil Wire at Bottom', val: coilBottom, set: setCoilBottom, avail: true,
                    conflict: setBottomRail, disabled: bottomRail && !coilBottom,
                    hint: bottomRail && !coilBottom ? 'Bottom Rail is selected' : null },
                  { label: 'Barb Wire', val: barbWire, set: setBarbWire, avail: barbWireAvail },
                ].map(o => {
                  const blocked = !o.avail || o.disabled;
                  const toggle = () => {
                    if (blocked) return;
                    if (!o.val && o.conflict) o.conflict(false);
                    o.set(!o.val);
                  };
                  return (
                    <label key={o.label}
                      title={o.disabled && o.hint ? `${o.hint} — only one can be selected` : ''}
                      className="flex items-center gap-3 p-3 rounded-xl"
                      style={{
                        border: `1px solid ${o.val ? '#0077B6' : '#E3ECF5'}`,
                        background: o.val ? '#E6F1FB' : '#FFFFFF',
                        opacity: blocked ? 0.4 : 1,
                        pointerEvents: blocked ? 'none' : 'auto',
                        cursor: blocked ? 'not-allowed' : 'pointer',
                      }}>
                      <input type="checkbox" checked={o.val} onChange={toggle} disabled={blocked} style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                      <span style={{ color: '#03045E', fontWeight: 600, fontSize: 14 }}>{o.label}</span>
                      {o.hint && <span style={{ color: '#6B7C93', fontSize: 12, marginLeft: 4, fontStyle: 'italic' }}>{o.hint}</span>}
                    </label>
                  );
                })}
              </div>
              {!barbWireAvail && (
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0' }}>Barb wire only available for Heavy (Commercial) fence 5' or taller</p>
              )}
              {effectiveBarbWire && (
                <div style={{ marginTop: 8, background: '#FDF1DD', border: '1px solid #EBD9B4', borderRadius: 10, padding: '8px 12px' }}>
                  <p style={{ color: '#854F0B', fontSize: 14, fontWeight: 600, margin: 0 }}>⚠ Barb Wire selected — end/corner post options updated for longer posts</p>
                </div>
              )}
            </div>

            {/* Post Spacing */}
            <div className="mb-5" style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16 }}>
              <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Post Spacing</label>
              <div className="flex items-center gap-3">
                <input type="number" value={postSpacing}
                  onChange={e => setPostSpacing(parseFloat(e.target.value) || 10)}
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: 112, textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#03045E', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                  min="1" max="20" />
                <span style={{ color: '#6B7C93', fontSize: 14 }}>feet between line posts <span style={{ color: '#9AA7B8' }}>(default: 10 ft)</span></span>
              </div>
            </div>

            {/* Post Dropdowns */}
            <div className="mb-5">
              <p style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 12px 0' }}>Posts</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>End Posts</label>
                  <select value={endPostSize} onChange={e => setEndPostSize(e.target.value)}
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                    {endCornerOpts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Corner Posts</label>
                  <select value={cornerPostSize} onChange={e => setCornerPostSize(e.target.value)}
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                    {endCornerOpts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Line Posts</label>
                  <select value={linePostSize} onChange={e => setLinePostSize(e.target.value)}
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                    {lineOpts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Summary chips */}
            {screen2Summary.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 mb-4">
                {screen2Summary.map(s => (
                  <span key={s} style={{ background: '#E6F1FB', color: '#0077B6', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>{s}</span>
                ))}
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button onClick={() => setStep(1)}
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 600, padding: '12px 24px', borderRadius: 12, fontSize: 15, cursor: 'pointer' }}>← Back</button>
              <button onClick={() => setStep(3)}
                disabled={!endPostSize || !linePostSize}
                style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 32px', borderRadius: 12, fontSize: 15, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', opacity: (!endPostSize || !linePostSize) ? 0.5 : 1 }}>
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ══════════ SCREEN 3 ══════════ */}
        {step === 3 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 24 }}>
            <h2 style={{ color: '#03045E', fontSize: 20, fontWeight: 700, margin: 0 }}>Step 3 — Dimensions</h2>
            <p style={{ color: '#6B7C93', fontSize: 14, margin: '4px 0 20px 0' }}>Enter run lengths, post counts, tie-ons, and gates.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
              {/* Runs */}
              <div>
                <p style={{ color: '#03045E', fontWeight: 700, margin: '0 0 12px 0' }}>Runs</p>
                <div className="space-y-2 mb-2">
                  {runs.map((run, idx) => (
                    <div key={run.id} className="flex items-center gap-2">
                      <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Run {idx + 1}</span>
                      <input
                        type="number"
                        value={run.length === 0 ? '' : run.length}
                        ref={el => { runInputRefs.current[run.id] = el; }}
                        onChange={e => updateRun(run.id, e.target.value)}
                        onKeyDown={handleRunKeyDown}
                        onFocus={e => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                        style={{ flex: 1, border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px', fontSize: 18, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                        placeholder="ft"
                        min="1"
                      />
                      <span style={{ color: '#6B7C93', fontSize: 12 }}>ft</span>
                      {runs.length > 1 && (
                        <button onClick={() => removeRun(run.id)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontWeight: 700, padding: '0 4px', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={addRun} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
                  + Add Run
                </button>
                {totalFootage > 0 && (
                  <div style={{ marginTop: 12, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: 12 }}>
                    <p style={{ color: '#03045E', fontWeight: 700, margin: 0 }}>Total: {totalFootage.toLocaleString()} ft</p>
                    <p style={{ color: '#0077B6', fontSize: 12, margin: '2px 0 0 0' }}>{runs.filter(r => parseFloat(r.length) > 0).length} run(s)</p>
                  </div>
                )}
              </div>

              {/* Post Counts */}
              <div>
                <p style={{ color: '#03045E', fontWeight: 700, margin: '0 0 12px 0' }}>Total Post Counts</p>
                <div className="space-y-3">
                  <div style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 12, padding: 12 }}>
                    <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>END POSTS</label>
                    <input type="number"
                      value={totalEnds === 0 ? '' : totalEnds}
                      onChange={e => {
                        const val = e.target.value;
                        setTotalEnds(val === '' ? 0 : parseInt(val) || 0);
                      }}
                      onFocus={e => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="0"
                      style={{ width: '100%', textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#03045E', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                      min="0" />
                  </div>
                  <div style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 12, padding: 12 }}>
                    <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>CORNER POSTS</label>
                    <input type="number"
                      value={totalCorners === 0 ? '' : totalCorners}
                      onChange={e => {
                        const val = e.target.value;
                        setTotalCorners(val === '' ? 0 : parseInt(val) || 0);
                      }}
                      onFocus={e => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                      onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                      placeholder="0"
                      style={{ width: '100%', textAlign: 'center', fontSize: 24, fontWeight: 700, color: '#03045E', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                      min="0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Tie-Ons */}
            <div className="mb-5">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input type="checkbox" checked={tieOnsEnabled} onChange={() => setTieOnsEnabled(!tieOnsEnabled)} style={{ width: 16, height: 16, accentColor: '#0077B6' }} />
                <span style={{ color: '#03045E', fontWeight: 600, fontSize: 14 }}>Add tie-on posts (connect to existing posts)</span>
              </label>
              {tieOnsEnabled && (
                <div style={{ border: '1px solid #E3ECF5', borderRadius: 12, padding: 16, background: '#CDE7F5' }}>
                  {tieOns.length === 0
                    ? <p style={{ color: '#6B7C93', fontSize: 14, margin: '0 0 12px 0' }}>No tie-on posts added.</p>
                    : (
                      <div className="space-y-2 mb-3">
                        {tieOns.map((t, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#FFFFFF', borderRadius: 10, padding: 8, border: '1px solid #E3ECF5' }}>
                            <div className="flex-1">
                              <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, marginBottom: 4 }}>Diameter</label>
                              <select value={t.diameter} onChange={e => updateTieOn(i, 'diameter', e.target.value)}
                                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                                style={{ width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '6px 8px', fontSize: 14, fontWeight: 600, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                                {TIE_ON_DIAMETERS.map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>
                            <div className="w-20">
                              <label style={{ display: 'block', color: '#6B7C93', fontSize: 11, marginBottom: 4 }}>Qty</label>
                              <input type="number" min="0"
                                value={t.qty === 0 ? '' : t.qty}
                                onChange={e => {
                                  const val = e.target.value;
                                  updateTieOn(i, 'qty', val === '' ? 0 : parseInt(val) || 0);
                                }}
                                onFocus={e => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                                placeholder="0"
                                style={{ width: '100%', textAlign: 'center', border: '1px solid #E3ECF5', borderRadius: 10, padding: '6px 8px', fontSize: 14, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                            </div>
                            <button onClick={() => removeTieOn(i)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontWeight: 700, fontSize: 18, marginTop: 16, cursor: 'pointer' }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  <button onClick={addTieOn} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>+ Add Tie-On</button>
                </div>
              )}
            </div>

            {/* Gates */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <p style={{ color: '#03045E', fontWeight: 700, margin: 0 }}>Gates</p>
                <button onClick={openAddGate}
                  style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '8px 16px', borderRadius: 10, cursor: 'pointer' }}>
                  + Add Gate
                </button>
              </div>
              {gates.length === 0 ? (
                <p style={{ color: '#6B7C93', fontSize: 14, margin: 0 }}>No gates added. Click "+ Add Gate" to add one.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ border: '1px solid #E3ECF5', borderRadius: 12, overflow: 'hidden' }}>
                    <thead style={{ background: '#E6F1FB' }}>
                      <tr>
                        <th className="px-3 py-2 text-left" style={{ color: '#6B7C93', fontWeight: 600 }}>#</th>
                        <th className="px-3 py-2 text-left" style={{ color: '#6B7C93', fontWeight: 600 }}>Type</th>
                        <th className="px-3 py-2 text-left" style={{ color: '#6B7C93', fontWeight: 600 }}>Opening</th>
                        <th className="px-3 py-2 text-left" style={{ color: '#6B7C93', fontWeight: 600 }}>Post Size</th>
                        <th className="px-3 py-2 text-center" style={{ color: '#6B7C93', fontWeight: 600 }}>Posts</th>
                        <th className="px-3 py-2 text-center" style={{ color: '#6B7C93', fontWeight: 600 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gates.map((gate, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#CDE7F5', borderTop: '1px solid #E3ECF5' }}>
                          <td className="px-3 py-2" style={{ fontWeight: 700, color: '#03045E' }}>{i + 1}</td>
                          <td className="px-3 py-2" style={{ color: '#03045E' }}>{gate.type}</td>
                          <td className="px-3 py-2" style={{ color: '#03045E' }}>{gate.opening ? `${gate.opening}"` : '—'}</td>
                          <td className="px-3 py-2 text-xs" style={{ color: '#03045E' }}>{gate.postSize}</td>
                          <td className="px-3 py-2 text-center" style={{ color: '#6B7C93' }}>{gate.type === 'Cantilever' ? 3 : 2}</td>
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => openEditGate(i)} style={{ background: 'none', border: 'none', color: '#0077B6', marginRight: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
                            <button onClick={() => setGates(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#A32D2D', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Summary */}
            {totalFootage > 0 && (
              <div style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16, marginBottom: 20 }}>
                <p style={{ color: '#03045E', fontWeight: 700, margin: '0 0 8px 0', fontSize: 14 }}>Job Summary</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Footage', val: `${totalFootage.toLocaleString()} ft`, color: '#0077B6' },
                    { label: 'End Posts', val: totalEnds, color: '#0077B6' },
                    { label: 'Corner Posts', val: totalCorners, color: '#0077B6' },
                    { label: 'Gates', val: gates.length, color: '#0F6E56' },
                  ].map(item => (
                    <div key={item.label} className="text-center">
                      <p style={{ color: '#6B7C93', fontSize: 12, margin: 0 }}>{item.label}</p>
                      <p style={{ color: item.color, fontWeight: 700, fontSize: 20, margin: 0 }}>{item.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-2">
              <button onClick={() => setStep(2)}
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 600, padding: '12px 24px', borderRadius: 12, fontSize: 15, cursor: 'pointer' }}>← Back</button>
              <button onClick={handleCalculate} disabled={!canCalc}
                style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 32px', borderRadius: 12, fontSize: 17, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', opacity: !canCalc ? 0.5 : 1 }}>
                🔧 Calculate Materials →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══════════ GATE MODAL (Screen 4) ══════════ */}
      {gateModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(3,4,94,0.35)' }}>
          <div className="w-full max-w-lg" style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, boxShadow: '0 20px 50px rgba(3,4,94,0.18)', overflow: 'hidden' }}>
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E3ECF5' }}>
              <h3 style={{ color: '#03045E', fontWeight: 700, fontSize: 18, margin: 0 }}>Step 4 — Gate Specifications</h3>
              <button onClick={() => setGateModalOpen(false)} style={{ background: 'none', border: 'none', color: '#6B7C93', fontSize: 24, lineHeight: 1, cursor: 'pointer' }}>×</button>
            </div>

            <div className="p-6 space-y-4">
              {/* Gate Type */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Gate Type</label>
                <div className="flex flex-wrap gap-2">
                  {['Single Swing', 'Double Swing', ...(fenceClass === 'Commercial' ? ['Cantilever'] : [])].map(t => {
                    const on = gateForm.type === t;
                    return (
                    <button key={t} onClick={() => handleGateTypeChange(t)}
                      style={{ padding: '8px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s',
                        background: on ? '#0077B6' : '#FFFFFF', color: on ? '#FFFFFF' : '#6B7C93', border: `1px solid ${on ? '#0077B6' : '#E3ECF5'}` }}>
                      {t}
                    </button>
                    );
                  })}
                </div>
              </div>

              {/* Gate Opening */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Gate Opening (inches)</label>
                <input type="number" min="1"
                  value={gateForm.opening === 0 ? '' : gateForm.opening}
                  onChange={e => handleGateOpeningChange(e.target.value)}
                  onFocus={e => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '10px 16px', fontSize: 18, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                  placeholder="e.g. 48" />
                {gateForm.opening && (
                  <>
                    <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0' }}>= {(parseFloat(gateForm.opening) / 12).toFixed(1)} ft</p>
                    {fenceClass === 'Commercial' && parseFloat(gateForm.opening) >= 312 && (
                      <div style={{ marginTop: 8, background: '#FDF1DD', border: '1px solid #EBD9B4', borderRadius: 10, padding: '8px 12px' }}>
                        <p style={{ color: '#854F0B', fontSize: 14, fontWeight: 600, margin: 0 }}>⚠ Large opening — Commercial Double Swing recommended. 6-5/8" post options available (Galvanized only).</p>
                      </div>
                    )}
                  </>
                )}
                {/* Cantilever SKU preview — shows which standard gate the opening maps to */}
                {gateForm.type === 'Cantilever' && parseFloat(gateForm.opening) > 0 && (() => {
                  const result = getCantileverGateSKU(gateForm.opening, height);
                  if (result.error) {
                    return (
                      <div style={{ marginTop: 8, background: '#FBECEC', border: '1px solid #E9C3C3', borderRadius: 10, padding: '8px 12px' }}>
                        <p style={{ color: '#A32D2D', fontSize: 14, fontWeight: 600, margin: 0 }}>⚠ {result.error}</p>
                      </div>
                    );
                  }
                  const enteredFt = (parseFloat(gateForm.opening) / 12).toFixed(2);
                  const rounded = result.openingFt * 12 !== parseFloat(gateForm.opening) || result.heightFt !== parseFloat(height);
                  return (
                    <div style={{ marginTop: 8, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px' }}>
                      <p style={{ color: '#03045E', fontSize: 14, fontWeight: 600, margin: 0 }}>
                        📦 Will use <span style={{ fontWeight: 800, color: '#0077B6' }}>{result.sku}</span> — {result.openingFt}' × {result.heightFt}' Cantilever Slide Gate Galv
                      </p>
                      {rounded && (
                        <p style={{ color: '#854F0B', fontSize: 12, margin: '4px 0 0 0' }}>↑ Rounded up from {enteredFt}' opening / {height}' height to next available size</p>
                      )}
                    </div>
                  );
                })()}
                {/* Swing gate preview — shows the standard size the opening rounds up to */}
                {(gateForm.type === 'Single Swing' || gateForm.type === 'Double Swing') && parseFloat(gateForm.opening) > 0 && (() => {
                  const preview = getSwingGateSize(gateForm.opening, height, gateForm.type, fenceClass, finish === 'Black');
                  const enteredFt = (parseFloat(gateForm.opening) / 12).toFixed(2);
                  if (preview.outOfRange) {
                    return (
                      <div style={{ marginTop: 8, background: '#FDF1DD', border: '1px solid #EBD9B4', borderRadius: 10, padding: '8px 12px' }}>
                        <p style={{ color: '#854F0B', fontSize: 14, fontWeight: 700, margin: 0 }}>⚠️ Too large for {gateForm.type}</p>
                        <p style={{ color: '#9A6B2E', fontSize: 12, margin: '2px 0 0 0' }}>
                          Maximum {gateForm.type} opening is {preview.openingLabel} — the largest available size will be used.
                          {gateForm.type === 'Single Swing' && ' Consider Double Swing for larger openings.'}
                        </p>
                      </div>
                    );
                  }
                  if (preview.wasRounded) {
                    return (
                      <div style={{ marginTop: 8, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px' }}>
                        <p style={{ color: '#03045E', fontSize: 14, fontWeight: 600, margin: 0 }}>
                          📦 Will add <span style={{ fontWeight: 800, color: '#0077B6' }}>{preview.openingLabel} × {preview.heightLabel} {gateForm.type}</span>
                        </p>
                        <p style={{ color: '#854F0B', fontSize: 12, margin: '4px 0 0 0' }}>↑ Rounded up from {enteredFt}' opening to next available size</p>
                      </div>
                    );
                  }
                  return (
                    <p style={{ marginTop: 8, color: '#0F6E56', fontSize: 14, fontWeight: 600 }}>
                      ✓ Exact match: {preview.openingLabel} × {preview.heightLabel} {gateForm.type}
                    </p>
                  );
                })()}
              </div>

              {/* Gate Post Size (Hinge / both sides) */}
              <div>
                <label style={{ display: 'block', color: '#6B7C93', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  {gateForm.type === 'Cantilever' ? 'Gate Post Size' : 'Gate Post Size (Hinge Side)'}
                </label>
                <select value={gateForm.postSize}
                  onChange={e => setGateForm(f => ({ ...f, postSize: e.target.value }))}
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                  {gatePostOpts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* Latch Post Size (Single Swing only) */}
              {gateForm.type === 'Single Swing' && (
                <div>
                  <label style={{ display: 'block', color: '#6B7C93', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Latch Post Size</label>
                  <select value={gateForm.latchPostSize}
                    onChange={e => setGateForm(f => ({ ...f, latchPostSize: e.target.value }))}
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 12px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, fontWeight: 600, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', cursor: 'pointer', transition: 'border-color 0.3s, box-shadow 0.3s' }}>
                    {gatePostOpts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              )}

              {/* Gate Posts Count info */}
              <div style={{ background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 10, padding: 12, fontSize: 14, color: '#6B7C93' }}>
                <span style={{ fontWeight: 700, color: '#03045E' }}>Posts needed:</span>{' '}
                {gateForm.type === 'Cantilever' ? '3 gate posts' : '2 gate posts'}
                {gateForm.type === 'Cantilever' && <span style={{ color: '#0077B6', marginLeft: 8 }}>(Cantilever — Commercial only)</span>}
              </div>
            </div>

            <div className="px-6 pb-6 flex justify-end gap-3">
              <button onClick={() => setGateModalOpen(false)}
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 600, padding: '10px 20px', borderRadius: 12, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveGate}
                disabled={!gateForm.opening || !gateForm.postSize}
                style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '10px 24px', borderRadius: 12, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', opacity: (!gateForm.opening || !gateForm.postSize) ? 0.5 : 1 }}>
                Save Gate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Estimator;
